import { Component, OnInit } from '@angular/core';
import { BentoAlertItemOptions } from '@bento/bento-ng';
import { getMeta, getBindings } from '@onviobr/services/session';

/* Important message to display to the user */
interface ImportantMessage {
	/* Unique identifier for permanently hiding/confirming */
	id: string;
	/* Optional (recommended) IAC to control showing of the message */
	iac?: string;
	/* Message to show (text or html) */
	content: string;
	/* Index (dynamically assigned) */
	index?: number;
}

/**
 * Displays important messages to the user
 */
@Component({
	selector: 'app-important-message',
	template: '<div class="ng-bento"><bento-alert [items]="display" (close)="messageClosed($event)"></bento-alert></div>'
})
export class ImportantMessageComponent implements OnInit {

	/* Messages to display */
	public display: BentoAlertItemOptions[] = [];

	/* All messages */
	public messages: ImportantMessage[] = [];

	/* Allow messages to be hidden on a per-user basis */
	public userId: string;

	public getMeta = getMeta;
	public getBindings = getBindings;

	public constructor() {

		// COVID-19 (aka Coronavirus) - APRIL 2020
		this.messages.push({
			id: 'covid19',
			iac: 'OnvioCenterDisplayCOVIDAlert',
			/* eslint-disable-next-line max-len */
			content: 'See the <a href="https://tax.thomsonreuters.com/en/accounting-solutions/c/covid-19" target="_blank">COVID-19 Tax and Accounting resources</a> page for guidance on navigating the current crisis.'
		});

	}

	async ngOnInit() {

		const iacs = await (await this.getBindings()).InfrastructureAccessControls;
		this.userId = await (await this.getMeta()).EmailAddress.toLowerCase();

		this.messages.forEach(message => {
			if (this.shouldDisplayMessage(message.iac, iacs) && !this.isMessageHidden(message.id, this.userId)) {
				message.index = this.display.push({
					type: 'info',
					msg: message.content,
					closeable: true
				}) - 1;
			}
		});

	}

	/**
	 * Occurs when the user clicks to close the message
	 *
	 * @param index Bento sends the current index of the alert
	 */
	public messageClosed(index: number): void {
		// Hide the message
		this.hideMessage(this.messages.find(message => message.index === index).id, this.userId);
		// Re-order the indexes due to the way bento handles this
		this.messages.forEach(m => m.index = m.index === index ? undefined : (m.index > index) ? m.index - 1 : m.index);
	}

	/**
	 * Permanently hides the message for this browser
	 *
	 * @param id Identifier of the message to hide
	 * @param userId User id
	 */
	public hideMessage(id: string, userId: string): void {
		window.localStorage.setItem(`message-${id}-${userId}`, 'hide');
	}

	/**
	 * Returns true if the message has previously been permanetly hidden
	 *
	 * @param id Identifier of the message to check
	 * @param userId User id
	 */
	public isMessageHidden(id: string, userId: string): boolean {
		return window.localStorage.getItem(`message-${id}-${userId}`) === 'hide';
	}

	/**
	 * Returns true if no iac is specified, or if IACs array contains the iac
	 *
	 * @param iac IAC to check
	 * @param IACs Array of available IACs
	 */
	public shouldDisplayMessage(iac: string, IACs: string[]): boolean {
		return iac === undefined || IACs.indexOf(iac) >= 0;
	}

}
